// src/pages/DextPage.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload, FileText, CheckCircle, X, ArrowRight, BarChart2,
  Download, ExternalLink, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { uploadReport, getReports } from '../utils/api';

const EXPORT_STEPS = [
  {
    num: '1',
    title: 'Open Dext → Bank',
    desc: 'Log in to app.dext.com, then click "Bank" in the left sidebar.',
  },
  {
    num: '2',
    title: 'Select the account',
    desc: 'Click "Transactions" on the bank account you want to analyze (e.g. New Epay, Expense ATM).',
  },
  {
    num: '3',
    title: 'Set the date range',
    desc: 'Use the date filter to select the last 30 days (or any period you want analyzed).',
  },
  {
    num: '4',
    title: 'Click "Export all"',
    desc: 'Hit the "Export all" button at the top of the transactions table to download a CSV file.',
  },
  {
    num: '5',
    title: 'Upload the CSV below',
    desc: 'Drop or select the downloaded CSV file in the upload zone below to run the AI analysis.',
  },
];

export default function DextPage() {
  const [file, setFile]           = useState(null);
  const [done, setDone]           = useState(null);
  const [processing, setProcessing] = useState(null);
  const navigate  = useNavigate();
  const qc        = useQueryClient();
  const pollRef   = useRef(null);

  // Poll for completed report after background job starts
  useEffect(() => {
    if (!processing) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    const MAX_POLL_MS = 15 * 60 * 1000;
    pollRef.current = setInterval(async () => {
      try {
        const data     = await getReports(100);
        const reports  = data?.reports || data || [];
        const elapsed  = Date.now() - processing.startedAt.getTime();
        const matches  = reports.filter(r =>
          r.runId === processing.runId ||
          (r.inputFilename === processing.filename && new Date(r.processedAt) >= processing.startedAt)
        );
        if (matches.length >= 1 || elapsed > MAX_POLL_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setProcessing(null);
          if (matches.length >= 1) {
            setDone({ report: matches[0] });
            toast.success('Analysis complete — report saved!');
            qc.invalidateQueries({ queryKey: ['reports'] });
          } else {
            toast.error('Analysis timed out. Check Run Log.');
          }
        }
      } catch {}
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [processing, qc]);

  const mutation = useMutation({
    mutationFn: () => uploadReport(file, false),
    onSuccess: (data) => {
      if (data.status === 'processing') {
        setProcessing({
          runId:     data.runId,
          filename:  data.filename || file?.name,
          startedAt: new Date(),
        });
      } else if (data.status === 'success') {
        setDone({ report: data });
        toast.success('Analysis complete!');
        qc.invalidateQueries({ queryKey: ['reports'] });
      } else {
        toast.error('Processing failed: ' + (data.error || 'Unknown error'));
      }
    },
    onError: (err) => toast.error(err.response?.data?.error || err.message),
  });

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) { setFile(accepted[0]); setDone(null); }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
  });

  function reset() { setFile(null); setDone(null); setProcessing(null); mutation.reset(); }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dext Bank Analysis</h1>
        <p className="page-subtitle">
          Export your bank transactions from Dext, then upload the CSV to run an AI analysis.
        </p>
      </div>

      {/* ── How to export from Dext ── */}
      {!done && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>
              Step-by-step: Export from Dext
            </div>
            <a
              href="https://app.dext.com/delta/bank/accounts"
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary btn-sm"
            >
              <ExternalLink size={12} /> Open Dext
            </a>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {EXPORT_STEPS.map((step, i) => (
              <div
                key={step.num}
                style={{
                  display: 'flex', gap: 14, padding: '12px 0',
                  borderBottom: i < EXPORT_STEPS.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                  background: i === 3 ? 'rgba(226,185,111,0.25)' : 'rgba(226,185,111,0.1)',
                  color: 'var(--gold)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                  border: i === 3 ? '1px solid rgba(226,185,111,0.5)' : '1px solid transparent',
                }}>
                  {step.num}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{step.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Visual callout for the Export All button */}
          <div style={{
            marginTop: 16, background: 'rgba(226,185,111,0.07)',
            border: '1px solid rgba(226,185,111,0.25)', borderRadius: 10,
            padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <Download size={16} style={{ color: 'var(--gold)', flexShrink: 0 }} />
            <span style={{ fontSize: 13 }}>
              Look for the{' '}
              <strong style={{
                background: 'rgba(226,185,111,0.15)', border: '1px solid rgba(226,185,111,0.4)',
                borderRadius: 5, padding: '1px 8px', fontSize: 12,
              }}>
                Export all
              </strong>
              {' '}button at the top-left of the Transactions table — it downloads a CSV with all visible transactions.
            </span>
          </div>
        </div>
      )}

      {/* ── Upload zone ── */}
      {!done && !processing && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title" style={{ marginBottom: 14 }}>Upload Dext Export</div>

          <div {...getRootProps()} className={`dropzone ${isDragActive ? 'drag-over' : ''}`}>
            <input {...getInputProps()} />
            <div className="dropzone-icon"><Upload size={36} /></div>
            {file ? (
              <>
                <div className="dropzone-label" style={{ color: 'var(--gold)' }}>
                  <FileText size={16} style={{ display: 'inline', marginRight: 6 }} />
                  {file.name}
                </div>
                <div className="dropzone-sub">{(file.size / 1024).toFixed(1)} KB — click or drop to replace</div>
              </>
            ) : (
              <>
                <div className="dropzone-label">Drop the Dext CSV export here</div>
                <div className="dropzone-sub">Supports .csv, .xlsx, .xls — max 20 MB</div>
              </>
            )}
          </div>

          {file && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                {mutation.isPending
                  ? <><span className="spinner" /> Uploading…</>
                  : <><BarChart2 size={15} /> Run AI Analysis</>}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={reset}>
                <X size={13} /> Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Processing indicator ── */}
      {processing && !mutation.isPending && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, borderTop: '3px solid var(--gold)' }}>
          <span className="spinner" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Claude is analyzing your transactions…</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              This takes 30–90 seconds. The page will update automatically when done.
            </div>
            <div style={{ color: 'var(--gold)', fontSize: 12, marginTop: 6 }}>
              File: <strong>{processing.filename}</strong>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={reset} style={{ flexShrink: 0 }}>
            <X size={12} /> Cancel
          </button>
        </div>
      )}

      {mutation.isPending && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span className="spinner" />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Uploading…</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              Sending file to the server.
            </div>
          </div>
        </div>
      )}

      {/* ── Success callout ── */}
      {done && done.report && (
        <div className="card" style={{ borderColor: 'rgba(63,185,80,0.4)', borderTop: '3px solid var(--green)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <CheckCircle size={28} style={{ color: 'var(--green)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Analysis complete!</div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'var(--navy-3)', borderRadius: 8, padding: '10px 14px',
                marginBottom: 14, fontSize: 13, flexWrap: 'wrap',
              }}>
                <FileText size={13} style={{ color: 'var(--text-muted)' }} />
                <span style={{ color: 'var(--text-muted)' }}>Input:</span>
                <strong>{done.report.inputFilename || file?.name}</strong>
                <ArrowRight size={14} style={{ color: 'var(--gold)' }} />
                <FileText size={13} style={{ color: 'var(--gold)' }} />
                <span style={{ color: 'var(--text-muted)' }}>Output:</span>
                <strong style={{ color: 'var(--gold)' }}>{done.report.outputFilename || 'Analysis report'}</strong>
              </div>

              <div style={{
                background: 'rgba(226,185,111,0.08)', border: '1px solid rgba(226,185,111,0.25)',
                borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <BarChart2 size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <span>
                  Report saved. View and download it in{' '}
                  <strong style={{ color: 'var(--gold)' }}>Analyzed Reports</strong>.
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => navigate('/reports')}>
                  <BarChart2 size={14} /> View Reports
                </button>
                <button className="btn btn-secondary btn-sm" onClick={reset}>
                  Analyze another file
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
